﻿<!--
  EmployerSubnav - section-nav tab strip rendered below the shared
  JobsProfileNavbar for authenticated employer routes.

  Approval gating: Inbox requires an approved employer account (see
  router.ts meta.requiresApproval + EmployerLayout.enforceApprovalGuard).
  Surface that state visibly here instead of silently redirecting.

  Styled with Tailwind utilities + project tokens - no scoped CSS.
-->
<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useEmployerSessionStore } from '../../stores/employerSession';
import { useEmployerMessagesStore } from '../../stores/employerMessages';
import NavCountBadge from '../NavCountBadge.vue';

declare const window: any;
const BASE = (window.__APP_BASE__ || '/').replace(/\/+$/, '');

const route = useRoute();
const session = useEmployerSessionStore();
const messages = useEmployerMessagesStore();

// Start the badge (poll + SignalR push) once the session is approved. The
// store's start() is idempotent, so re-runs on session refresh are free.
watch(() => session.isApproved, approved => { if (approved) messages.start(BASE); }, { immediate: true });

// Shared classes kept in one place so the active/inactive/disabled variants stay aligned.
// `relative` anchors NavCountBadge, which is absolute so a live count can never
// reflow the tabs after it.
const baseTab = 'relative inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap';
const activeTab = 'border-[color:var(--jb-primary)] text-[color:var(--jb-primary)]';
const inactiveTab = 'border-transparent text-ink-2 hover:text-ink';
const disabledTab = 'border-transparent text-ink-3 opacity-50 cursor-not-allowed pointer-events-none';

function tabClass(active: boolean, disabled = false) {
    if (disabled) return [baseTab, disabledTab];
    return [baseTab, active ? activeTab : inactiveTab];
}

const isDashboard = computed(() => route.name === 'employer-dashboard');
const isInbox = computed(() => route.name === 'employer-inbox');
const isMessages = computed(() => route.name === 'employer-messages');
const isCandidates = computed(() => route.name === 'employer-candidates');
const isProfile = computed(() => route.name === 'employer-profile');
const isTeam = computed(() => route.name === 'employer-team');
const isBilling = computed(() => route.name === 'employer-billing');

// Mirrors router meta.requiresApproval for employer-inbox. Keep in sync if
// more routes gain the same meta flag.
const inboxLocked = computed(() => !session.isApproved);
const inboxTitle = computed(() =>
    inboxLocked.value ? 'Your applicants list unlocks once your account is approved' : ''
);
// Separate string from the Applicants one: the Messages tab shares the same
// approval gate but not the same subject, and it was telling people their
// "applicants list" was locked when they hovered Messages.
const messagesTitle = computed(() =>
    inboxLocked.value ? 'Messaging unlocks once your account is approved' : ''
);

// Approval is the only hard block (router meta.requiresApproval): an unapproved
// employer has nothing to buy yet. An APPROVED employer without a plan must still
// reach the page, because the locked panel behind this tab is where the subscribe
// CTA lives. Disabling it there left the paywall reachable only by typing the URL.
const candidatesLocked = computed(() => !session.isApproved);
const candidatesTitle = computed(() =>
    candidatesLocked.value ? 'The talent pool unlocks once your account is approved' : ''
);

// Approved but unpaid: reachable, badged with a lock so the gate is still legible.
const candidatesNeedsPlan = computed(() => session.isApproved && !session.hasActivePlan);
</script>

<template>
    <!-- Pinned under the shared site navbar, which is itself sticky at top:0 with
         z-index 1030. Sticking at --jb-navbar-h parks this strip directly beneath
         it, and z-1020 keeps it under the navbar so the seam is hidden rather
         than overlapping. bg-white matters here: once it detaches it has page
         content scrolling behind it.

         Safe to stick from here: this is a direct child of the layout root, a
         sibling of the router-view transition rather than inside it, and no
         ancestor clips or scrolls. -->
    <div class="sticky top-[var(--jb-navbar-h)] z-[1020] bg-white border-b border-[color:var(--jb-border)]">
        <!-- thin-scrollbar: the global ::-webkit-scrollbar rule in site.css sets
             only `width`, so a horizontal bar here fell back to 15px and read as
             a slab under the active tab's 2px underline. See the utility in
             tailwind.css. -->
        <div class="max-w-[1128px] mx-auto px-6 flex gap-1 overflow-x-auto thin-scrollbar">
            <router-link :to="{ name: 'employer-dashboard' }" :class="tabClass(isDashboard)">
                <Icon name="chart-bar" :size="16" />
                <span>Home</span>
            </router-link>
            <router-link
                v-if="!inboxLocked"
                :to="{ name: 'employer-inbox' }"
                :class="tabClass(isInbox)"
            >
                <Icon name="inbox" :size="16" />
                <span>Applicants</span>
            </router-link>
            <span
                v-else
                :class="tabClass(false, true)"
                :title="inboxTitle"
                aria-disabled="true"
            >
                <Icon name="inbox" :size="16" />
                <span>Applicants</span>
            </span>
            <!-- Same approval gate as Inbox (router meta.requiresApproval). -->
            <router-link
                v-if="!inboxLocked"
                :to="{ name: 'employer-messages' }"
                :class="tabClass(isMessages)"
            >
                <Icon name="message-square" :size="16" />
                <span>Messages</span>
                <NavCountBadge :count="messages.unreadCount"
                               class="border border-[color:var(--jb-primary)]/20 bg-primary/10 text-primary" />
            </router-link>
            <span
                v-else
                :class="tabClass(false, true)"
                :title="messagesTitle"
                aria-disabled="true"
            >
                <Icon name="message-square" :size="16" />
                <span>Messages</span>
            </span>
            <router-link
                v-if="!candidatesLocked"
                :to="{ name: 'employer-candidates' }"
                :class="tabClass(isCandidates)"
                :title="candidatesNeedsPlan ? 'Subscribe to search the talent pool' : ''"
            >
                <Icon name="user-check" :size="16" />
                <span>Talent pool</span>
                <!-- Left in flow, unlike the unread badge. This resolves once with
                     the session fetch, before anyone is aiming at a tab, so
                     reserving space for it only bought a permanent visible gap. -->
                <Icon v-if="candidatesNeedsPlan" name="lock" :size="13" class="text-ink-3" />
            </router-link>
            <span
                v-else
                :class="tabClass(false, true)"
                :title="candidatesTitle"
                aria-disabled="true"
            >
                <Icon name="user-check" :size="16" />
                <span>Talent pool</span>
            </span>
            <router-link :to="{ name: 'employer-profile' }" :class="tabClass(isProfile)">
                <Icon name="building" :size="16" />
                <span>Company</span>
            </router-link>
            <router-link :to="{ name: 'employer-team' }" :class="tabClass(isTeam)">
                <Icon name="users" :size="16" />
                <span>Team</span>
            </router-link>
            <!-- Never gated: an employer must be able to reach billing even when suspended,
                 otherwise they can be charged with no way to cancel. -->
            <router-link :to="{ name: 'employer-billing' }" :class="tabClass(isBilling)">
                <Icon name="credit-card" :size="16" />
                <span>Billing</span>
            </router-link>
        </div>
    </div>
</template>
